import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Truck, ArrowRight, Compass, ClipboardCheck, Users, ShieldCheck } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1695222833131-54ee679ae8e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const journey = [
  { icon: Compass, title: "Choose Your Direction", text: "CDL, dispatching, or a behind-the-desk career." },
  { icon: ClipboardCheck, title: "Complete One Smart Intake", text: "Applicants only see the questions connected to the programs they select." },
  { icon: Users, title: "Meet With an Enrollment Specialist", text: "AAWTA reviews readiness, funding needs, qualifications, and next steps." },
];

export default function Home() {
  return (
    <div className="App">
      <Header />

      {/* Hero */}
      <section className="relative bg-navy text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Semi truck on the road" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 bg-magenta/20 border border-magenta/40 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6">
              <Truck size={14} /> Driven Together Education Program
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Your Next Move in <span className="text-magenta">Transportation</span> Starts Here.
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl">
              The AAWTA Driven Together Education Program helps individuals enter and advance in trucking,
              logistics, and supply chain through education, mentorship, workforce connections, career
              pathways, and trusted education partners.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/intake" data-testid="hero-find-pathway"
                className="inline-flex items-center justify-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-3.5 px-7 rounded-md transition-colors">
                Find My Pathway <ArrowRight size={18} />
              </Link>
              <Link to="/programs" data-testid="hero-explore-programs"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold py-3.5 px-7 rounded-md transition-colors">
                Explore Programs
              </Link>
            </div>
            <p className="mt-8 text-magenta font-display font-bold text-lg">One Intake. Multiple Career Pathways.</p>
          </div>
        </div>
      </section>

      {/* Journey */}
      <section className="bg-white py-20 lg:py-24" data-testid="journey-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-navy">The Applicant Journey</span>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold text-navy">A clear path from first step to enrollment.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {journey.map((s, i) => (
              <div key={s.title} className="bg-white p-8 rounded-lg border border-navy/10 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300"
                   data-testid={`journey-card-${i}`}>
                <div className="flex items-center justify-between mb-5">
                  <span className="w-12 h-12 rounded-md bg-magenta/10 text-magenta flex items-center justify-center">
                    <s.icon size={24} />
                  </span>
                  <span className="font-display text-4xl font-black text-navy/10">0{i + 1}</span>
                </div>
                <h3 className="font-display text-xl font-bold text-navy mb-2">{s.title}</h3>
                <p className="text-dark-text/75 text-sm leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/intake" data-testid="journey-start-intake"
              className="inline-flex items-center gap-2 bg-magenta hover:bg-magenta-dark text-white font-bold py-3.5 px-7 rounded-md transition-colors">
              Start Your Intake <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Value band */}
      <section className="bg-navy-light py-20" data-testid="value-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-navy">Education, mentorship, and workforce connections — built for the road ahead.</h2>
            <p className="mt-5 text-dark-text/75 leading-relaxed">
              Whether you are pursuing a Class A CDL, becoming a certified dispatcher, or building
              behind-the-desk business skills, AAWTA meets you where you are and helps you move forward.
            </p>
            <Link to="/programs" className="mt-6 inline-flex items-center gap-2 text-navy font-bold hover:text-magenta transition-colors">
              Explore all programs <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Truck, label: "CDL Pathway" },
              { icon: ClipboardCheck, label: "Certified Dispatcher" },
              { icon: ShieldCheck, label: "Trusted Partners" },
              { icon: Users, label: "Mentorship" },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-lg border border-navy/10 p-6 shadow-sm">
                <c.icon className="text-magenta mb-3" size={26} />
                <p className="font-display font-bold text-navy">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
